/**
 * notifications.service.ts
 *
 * Typed API call wrappers for the Notifications domain.
 * All methods use the shared apiClient (Axios instance with JWT interceptor).
 */
import { apiClient } from './apiClient';
import type { PaginatedResponse } from '@/types/api.types';

// ─── Domain types (mirrors server notifications.types.ts) ───────────────────

/**
 * Server-rendered display block attached to every notification row by both
 * `GET /notifications` and `GET /notifications/pop`. The backend builds the
 * legacy `<strong>{title}</strong><br>{body}` strings from the related
 * record (contact / comm_log / payment / appt / note) so the Alerts list
 * and the bottom-right popups can render the same content without
 * re-implementing the per-typeID switch.
 */
export interface NotificationDisplay {
  title: string;
  body: string;
  group: string;
  link: string | null;
}

export interface Notification {
  notificationId: number;
  accountId: number;
  typeId: number | null;
  authorId: number | null;
  userId: number | null;
  patientId: number | null;
  planId: number | null;
  paymentId: number | null;
  commLogId: number | null;
  apptId: number | null;
  contactId: number | null;
  message: string;
  createDate: string;
  endDate: string | null;
  isRead: boolean;
  isEmailed: boolean;
  deliveryType: number | null;
  authorFirstName?: string | null;
  authorLastName?: string | null;
  patientFirstName?: string | null;
  patientLastName?: string | null;
  /** Present on every row returned by the v5 API; optional in the type so
   *  older clients / fixtures without the enriched block still type-check. */
  display?: NotificationDisplay;
}

/** Convenience alias kept for the popup hook — `display` is non-optional in
 *  the response shape the /pop endpoint guarantees. */
export type PopNotification = Notification & { display: NotificationDisplay };

// ─── Service methods ────────────────────────────────────────────────────────

/**
 * Fetch a paginated list of notifications for the current user.
 */
export function getNotifications(
  page = 1,
  pageSize = 25,
  unreadOnly?: boolean,
): Promise<PaginatedResponse<Notification>> {
  return apiClient
    .get<PaginatedResponse<Notification>>('/notifications', {
      params: { page, pageSize, ...(unreadOnly ? { unreadOnly: 'true' } : {}) },
    })
    .then((r) => r.data);
}

/**
 * Fetch unread notifications created in the last 30 minutes for the current
 * user. Each row carries a server-rendered `display` block. Powers the
 * bottom-right pop-up toaster (mirrors legacy notification_display.php).
 */
export function getPopNotifications(): Promise<PopNotification[]> {
  return apiClient
    .get<{ data: PopNotification[]; meta: { count: number } }>('/notifications/pop')
    .then((r) => r.data.data);
}

/**
 * Fetch the unread notification count for the current user.
 */
export function getUnreadCount(): Promise<number> {
  return apiClient
    .get<{ data: { count: number } }>('/notifications/unread-count')
    .then((r) => r.data.data.count);
}

/**
 * Mark a single notification as read.
 */
export function markRead(id: number): Promise<void> {
  return apiClient
    .patch(`/notifications/${id}/read`)
    .then(() => undefined);
}

/**
 * Mark all notifications as read for the current user.
 */
export function markAllRead(): Promise<void> {
  return apiClient
    .patch('/notifications/mark-all-read')
    .then(() => undefined);
}

/**
 * Delete a notification.
 */
export function deleteNotification(id: number): Promise<void> {
  return apiClient
    .delete(`/notifications/${id}`)
    .then(() => undefined);
}
